import { z } from 'zod';

import { ApRecurrenceValueType, ApRecurrencyFrequency, DocumentType } from './generated/enums.js';

/**
 * Email classification objectives
 */
export const EmailObjective = {
  AP: 'AP',
  REIMBURSEMENT: 'REIMBURSEMENT',
  SPAM: 'SPAM',
  UNKNOWN: 'UNKNOWN',
} as const;
export type EmailObjective = (typeof EmailObjective)[keyof typeof EmailObjective];
/**
 * Objectives that require a document attachment to be processed.
 * Used to determine when to request document from user.
 */
export const DOCUMENT_REQUIRED_OBJECTIVES: EmailObjective[] = ['AP', 'REIMBURSEMENT'];

/**
 * Check if an objective requires a document attachment
 */
export function requiresDocumentAttachment(objective: EmailObjective): boolean {
  return DOCUMENT_REQUIRED_OBJECTIVES.includes(objective);
}

/**
 * Extracted metadata from email/attachments
 */
export const EmailExtractedMetadataSchema = z.object({
  vendorName: z.string().nullish(),
  vendorTaxId: z.string().nullish(),
  cnpj: z.string().nullish(),
  cpf: z.string().nullish(),
  invoiceNumber: z.string().nullish(),
  amount: z.string().nullish(),
  currency: z.string().nullish(),
  dueDate: z.string().nullish(),
  /** Requested payout date ("data para pagamento"), distinct from boleto vencimento */
  scheduledDate: z.string().nullish(),
  issueDate: z.string().nullish(),
  /** Short description of expense / invoice (no-document flows) */
  description: z.string().nullish(),
  /** How the user paid (cartão, PIX, dinheiro, etc.) */
  paymentMethod: z.string().nullish(),
});

export type EmailExtractedMetadata = z.infer<typeof EmailExtractedMetadataSchema>;

/**
 * Rich bill data extraction from email body text.
 * Curated subset of BillDocumentExtraction fields realistically extractable
 * from email body content. Maps cleanly to DocumentExtraction with a simple transformer.
 * All fields nullable for strict-mode compatibility.
 */
export const EmailBodyExtractionSchema = z.object({
  billData: z
    .object({
      issueDate: z.string().nullable(),
      dueDate: z.string().nullable(),
      scheduledDate: z.string().nullable(),
      documentNumber: z.string().nullable(),
      description: z.string().nullable(),
    })
    .nullable(),
  vendor: z
    .object({
      legalName: z.string().nullable(),
      tradeName: z.string().nullable(),
      taxId: z.string().nullable(),
      taxIdType: z.string().nullable(),
      /** ISO 3166-1 alpha-2 country of the vendor's address, when the body shows it. */
      countryCode: z.string().nullish(),
    })
    .nullable(),
  customer: z
    .object({
      legalName: z.string().nullable(),
      tradeName: z.string().nullable(),
      taxId: z.string().nullable(),
      taxIdType: z.string().nullable(),
    })
    .nullable()
    .optional(),
  financials: z
    .object({
      grossAmount: z.number().nullable(),
      netAmount: z.number().nullable(),
      currency: z.string().nullable(),
      discounts: z.number().nullable(),
      interestAmount: z.number().nullable(),
      fineAmount: z.number().nullable(),
    })
    .nullable(),
  lineItems: z
    .array(
      z.object({
        description: z.string(),
        quantity: z.number().nullable(),
        unitPrice: z.number().nullable(),
        netValue: z.number().nullable(),
      }),
    )
    .nullable(),
  paymentInfo: z
    .object({
      paymentMethod: z.string().nullable(),
      barcode: z.string().nullable(),
      pixCopiaECola: z.string().nullable(),
      bankName: z.string().nullable(),
      bankBranch: z.string().nullable(),
      bankAccount: z.string().nullable(),
      dueDate: z.string().nullable(),
      scheduledDate: z.string().nullable(),
      scheduledAmount: z.number().nullable(),
    })
    .nullable(),
  inferredFlags: z
    .object({
      isRecurring: z.boolean().nullable(),
      isReimbursement: z.boolean().nullable(),
      isAlreadyPaid: z.boolean().nullable(),
      isAutomaticBankPayment: z.boolean().nullable(),
      automaticBankPaymentConfidence: z.number().min(0).max(100).nullable(),
      automaticBankPaymentReasoning: z.string().nullable(),
      isUrgent: z.boolean().nullable(),
    })
    .nullable(),
  classificationHints: z
    .object({
      costCenterHint: z.string().nullable(),
      departmentHint: z.string().nullable(),
      categoryHint: z.string().nullable(),
      projectHint: z.string().nullable(),
    })
    .nullable(),
});

export type EmailBodyExtraction = z.infer<typeof EmailBodyExtractionSchema>;

/**
 * Free-form instructions or descriptions the user wrote in the email
 * (e.g. allocation hints, urgency, project name, cost center).
 */
export const USER_INSTRUCTIONS_MAX_LENGTH = 300;

/**
 * Email classification result from AI triage
 */
export const EmailClassificationSchema = z.object({
  /** Primary objective of the email */
  objective: z.nativeEnum(EmailObjective),
  /** Document type (aligned with DocumentType enum) */
  documentType: z.nativeEnum(DocumentType),
  /** Confidence score 0-1 */
  confidence: z.number().min(0).max(1),
  /** Index of primary attachment (0-based) */
  primaryAttachmentIndex: z.number().int().min(0),
  /**
   * Indices of auxiliary/supporting attachments (0-based).
   * These are contracts, agreements, or large supporting documents that can provide
   * vendor, dates, and amounts for bill creation. Only include if <5MB.
   */
  auxiliaryAttachmentIndices: z.array(z.number().int().min(0)).optional().default([]),
  /** Whether the email is spam */
  isSpam: z.boolean().optional(),
  /** Reason for spam classification */
  spamReason: z.string().optional(),
  /** Detected language */
  language: z.string().optional(),
  /** Brief summary of the email */
  summary: z.string().optional(),
  /** Extracted metadata from email content */
  extractedMetadata: EmailExtractedMetadataSchema.optional(),
  /**
   * Free-form instructions or descriptions the user wrote about the document
   * (e.g. "alocar para marketing", "esta conta é urgente", "projeto Q1").
   * Used as context for classification and extraction; prefer document-derived values when they conflict.
   */
  userInstructions: z.string().optional(),
  /**
   * AI-generated follow-up message when document is missing or more info needed.
   * Contextual and personalized based on email content.
   */
  followUpMessage: z.string().optional(),
  /**
   * Rich bill data extracted from the email body text.
   * Populated even when attachments exist (body may contain complementary info).
   * Null when no bill-relevant data is found in the body.
   */
  emailBodyExtraction: EmailBodyExtractionSchema.nullable().optional(),
});

export type EmailClassification = z.infer<typeof EmailClassificationSchema>;

/**
 * Contract/auxiliary document extraction output.
 * Used when extracting vendor, dates, and amounts from contracts or supporting documents.
 */
export const ContractExtractionSchema = z.object({
  vendorName: z.string().optional(),
  vendorTaxId: z.string().optional(),
  amount: z.string().optional(),
  currency: z.string().optional(),
  dueDate: z.string().optional(),
  issueDate: z.string().optional(),
  description: z.string().optional(),
  /** Value of each periodic payment, as opposed to `amount` (total contract value). */
  recurringAmount: z.number().optional(),
  recurrencyFrequency: z.enum(ApRecurrencyFrequency).optional(),
  recurrenceType: z.enum(ApRecurrenceValueType).optional(),
  expectedDueDayOfMonth: z.number().optional(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  totalInstallments: z.number().optional(),
});

export type ContractExtraction = z.infer<typeof ContractExtractionSchema>;

export interface EmailClassificationInput {
  subject?: string | null;
  bodyText?: string | null;
  bodyHtml?: string | null;
  fromAddress: string;
  attachments: {
    filename: string;
    mimeType: string;
    size: number;
  }[];
  /**
   * Conversation history for multi-turn conversations
   * Used when processing replies to pending conversations
   */
  conversationHistory?: ConversationHistoryEntry[];
}

/**
 * Entry in conversation history for context
 */
export interface ConversationHistoryEntry {
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp?: string;
  /** Fields that were requested in this turn */
  awaitingFields?: string[];
}
